import React, {useEffect, useMemo, useRef, useState} from 'react';
import {LineItem} from './LineItem.jsx';
import PropTypes from 'prop-types';
import {requestStores} from '../../ListOfPREditors.brick.jsx';
import {useFieldEditability} from '../utils/useFieldEditability.js';
import CircularProgress from '@mui/material/CircularProgress';
import {TablePaginationWithPageSelector} from '../../TablePaginationWithPageSelector.jsx';
import {BrickContext, CloudObject, Config, Query} from 'olympe';
import produce from 'immer';
import {AddTransportCostComponent} from './AddTransportCostComponent.jsx';
import {TransportCostsConditionTypePopup} from '../dialogs/TransportCostsConditionTypePopup.jsx';
import {CheckboxWithOptionsLineItem} from './CheckboxWithOptionsLineItem.jsx';
import {LAMBDA_NAMES, SOURCES, SOURCES_TO_DISPLAY, TRANSPORT_OPTIONS_ENUM} from '../../../utils/constants.js';
import AddIcon from '@mui/icons-material/Add';
import {ezprOrange} from '../../_helpers';
import {AddMaterialPopup} from '../dialogs/AddMaterialPopup.jsx';
import {
    getMasterDataLambda,
    getPunchoutCatalogsInfoLambda, getPunchoutCatalogURLLambda,
    getTaxAmountLambda,
    openCandexLambda, openNestBuyCatalogLambda,
} from '../../../utils/functionSignatures';
import {merge, skip, take} from 'rxjs';
import {PurchaseRequest} from '../../../models/PurchaseRequest';
import {map} from 'rxjs/operators';
import {MasterDataController} from '../../../controllers/MasterDataController';
import {ErrorPopup} from '../dialogs/ErrorPopup.jsx';
import {LoadingButton} from '@mui/lab';
import PurchaseRequestDM from '../../../models/cloudobjects/PurchaseRequestDM';

const NESTBUY_MATERIAL_POLL_INTERVAL_MS = 5000;
const NESTBUY_MATERIAL_POLL_MAX_DURATION_MS = 10 * 60 * 1000;

/**
 * @param {LineItems.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function LineItems(props) {
    const useRequestStore = requestStores.get(props.prTag);

    const [materials, updateMaterials] = useRequestStore((state) => [state.materials, state.updateMaterials]);
    const [addTransportCosts, addConditionTransportCost] = useRequestStore((state) => [state.addTransportCosts, state.addConditionTransportCost]);
    const userHasAccessToProperties = useRequestStore((state) => state.userHasAccessToProperties);
    const getPurchaseRequestFromStore = useRequestStore((state) => state.getPurchaseRequest);
    const currentPr = getPurchaseRequestFromStore().getPurchaseRequest();

    const source = useRequestStore((state) => state.source);
    const sourceToDisplay = useRequestStore((state) => state.sourceToDisplay);
    const syncCopiedItems = useRequestStore((state) => state.syncCopiedItems);
    const appendCopiedItems = useRequestStore((state) => state.appendCopiedItems);
    const deletedItemIds = useRequestStore((state) => state.deletedItemIds);
    const displayedMaterials = materials.filter((m) => !deletedItemIds.includes(m.id));
    const materialsToShow = (source === SOURCES.CANDEX) ? displayedMaterials : materials;
    const showAddMaterial = source !== SOURCES.CANDEX && source !== SOURCES.PROMO;

    const [loadingTransportCost, setLoadingTransportCost] = useState(false);
    const [loadingMaterial, setLoadingMaterial] = useState(false);
    const [storeLoadingMaterials, setStoreLoadingMaterials] = useRequestStore((state) => [state.loadingMaterials, state.setLoadingMaterials]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pageNum, setPageNum] = useState([]);
    const pageSize = rowsPerPage === -1 ? materials.length : (materials.length) / rowsPerPage;

    const updateAllMaterials = useRequestStore((state) => state.updateAllMaterials);
    const setMaterialsReady = useRequestStore((state) => state.setMaterialsReady);
    const updateAllMaterialsFunctional = useRequestStore((state) => state.updateAllMaterialsFunctional);
    const [selectedMaterialIds, setSelectedMaterialIds] = useState([]);
    const [selectedTransportCostMaterialId, setSelectedTransportCostMaterialId] = useState(null);

    const {isDisabled} = useFieldEditability(props.prTag, {context: props.context});

    const [showViaConditionPopup, setShowViaConditionPopup] = useState(false);
    const [resetSelection, setResetSelection] = useState(null);

    const [showAddMaterialPopup, setShowAddMaterialPopup] = useState(false);
    const [isTaxAmountLoading, setIsTaxAmountLoading] = useState(false);

    const [errorPopupOpen, setErrorPopupOpen] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [addMaterialLoading, setAddMaterialLoading] = useState(false);

    const [loadingCheckDependency, setLoadingCheckDependency] = useState(false);
    const isFirstRender = useRef(true);
    const nestBuyMaterialPollTimeoutRef = useRef(null);
    const nestBuyMaterialPollMaxTimeoutRef = useRef(null);
    const isNestBuyMaterialPollingRef = useRef(false);
    const getMasterData = props.context.get(LAMBDA_NAMES.GET_MASTER_DATA);
    const companyCode = useRequestStore((state) => state.companyCode);
    const purchaseOrganization = useRequestStore((state) => state.purchaseOrganization);
    const vendorCode = useRequestStore((state) => state.vendorCode);
    const vendorName = useRequestStore((state) => state.vendorName);
    const defaultPlant = useRequestStore((state) => state.defaultPlant);
    const defaultDeliveryAddress = useRequestStore((state) => state.defaultDeliveryAddress);
    const accounts = useRequestStore((state) => state.accounts);
    const areAccountSplitByMaterial = useRequestStore((state) => state.areAccountSplitByMaterial);
    const accountDetailsByMaterial = useRequestStore((state) => state.accountDetailsByMaterial);
    const userIdToCallSAPAPIs = useRequestStore((state) => state.userIdToCallSAPAPIs);
    const EPRId = useRequestStore((state) => state.EPRId);

    const openCandex = props.context.get(LAMBDA_NAMES.OPEN_CANDEX);
    const getTaxAmount = props.context.get(LAMBDA_NAMES.GET_TAX_AMOUNT_VERTEX);
    const getPunchOutCatalogs = props.context.get(LAMBDA_NAMES.GET_PUNCHOUT_CATALOGS);
    const getPunchOutCatalogURL = props.context.get(LAMBDA_NAMES.GET_PUNCHOUT_CATALOG_URL);
    const openNestBuyCatalog = props.context.get(LAMBDA_NAMES.OPEN_NESTBUY_CATALOG);

    useEffect(() => {
        if (rowsPerPage === -1) {
            setPage(0);
        } else if (materials?.length > 0 && materials?.length <= page * rowsPerPage) {
            setPage((current) => current - 1);
        }
    }, [materials, rowsPerPage, page]);

    useEffect(() => {
        const temp = [];
        if (rowsPerPage === -1) {
            temp.push(
                {
                    label: `1-${materials.length}`,
                    value: 1,
                },
            );
        } else {
            for (let i = 0; i < pageSize; i++) {
                temp.push({
                    label: `${(i * rowsPerPage) + 1}-${(i + 1) * rowsPerPage < materials.length ? (i + 1) * rowsPerPage : materials.length}`,
                    value: i + 1,
                });
            }
        }
        return setPageNum(temp);
    }, [pageSize]);

    // This useEffect create a subscription on the PR, to listen the PunchOut item added from the Define Supplier popup
    useEffect(() => {
        if (storeLoadingMaterials) {
            listenItemsFromPunchout();
        }
    }, [storeLoadingMaterials]);

    useEffect(() => {
        return () => {
            isNestBuyMaterialPollingRef.current = false;
            if (nestBuyMaterialPollTimeoutRef.current) {
                clearTimeout(nestBuyMaterialPollTimeoutRef.current);
                nestBuyMaterialPollTimeoutRef.current = null;
            }
            if (nestBuyMaterialPollMaxTimeoutRef.current) {
                clearTimeout(nestBuyMaterialPollMaxTimeoutRef.current);
                nestBuyMaterialPollMaxTimeoutRef.current = null;
            }
        };
    }, []);

    // This useEffect listens for company code changes and checks the dependencies
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (!companyCode.value) {
            return;
        }

        setLoadingCheckDependency(true);
        setMaterialsReady(false);

        const plantPromise = getMasterDataLambda(
            props.context,
            getMasterData,
            'Plant',
            '',
            companyCode.value,
            '',
            '',
            '',
            '',
            '',
            userIdToCallSAPAPIs,
        );

        const taxCodePromise = getMasterDataLambda(
            props.context,
            getMasterData,
            'Tax Code',
            '',
            companyCode.value,
            '',
            '',
            '',
            '',
            '',
            userIdToCallSAPAPIs,
        );

        Promise.all([plantPromise, taxCodePromise])
            .then((values) => {
                const formattedPlants = MasterDataController.formatMasterData(values[0], true, 'Plant');

                const formattedTaxCodes = MasterDataController.formatMasterData(values[1], true, 'Tax Code');

                const updatedMaterials = produce(materials, (draft) => {
                    draft.forEach((mat, index) => {
                        const plantExists = formattedPlants.some((p) => p.value === mat.plant.value);

                        if (!plantExists) {
                            mat.plant = {label: '', value: ''};
                            mat.deliveryAddress = {label: '', value: ''};
                            mat.storageLocation = {label: '', value: ''};
                        }

                        const taxCodeExists = formattedTaxCodes.some((tc) => tc.value === mat.taxCode.value);

                        if (!taxCodeExists) {
                            mat.taxCode = {label: '', value: ''};
                        }
                    });
                });

                updateAllMaterials(updatedMaterials);

                setLoadingCheckDependency(false);
                setMaterialsReady(true);
            })
            .catch((error) => {
                console.error('Error fetching master data:', error);
                setLoadingCheckDependency(false);
                setMaterialsReady(true);
            });
    }, [companyCode.value]);

    const materialI1Signature = useMemo(() => {
        if (companyCode.value.substring(0, 2) !== 'US') {
            return '';
        }
        return materials
            .filter((m) => m.taxCode?.value === 'I1')
            .map((m) => `${m.id}-${m.name}-${m.quantity}-${m.baseUnit}-${m.price}-${m.currency.value}-${m.per}-${m.plant.value}`)
            .join('|');
    }, [materials, companyCode.value]);

    const materialTaxDependencies = useMemo(() => {
        return materials.reduce((acc, material) => {
            let dep = '';
            if (areAccountSplitByMaterial) {
                const detail = accountDetailsByMaterial.find((d) => d.material_id === material.id);
                if (detail && detail.accounts && detail.accounts.length > 0) {
                    dep = detail.accounts
                        .map((account, accIndex) => {
                            const type = account.accountType?.value || '';
                            const gl = account.glNumber?.value || '';
                            if (!gl) {
                                return 'invalid';
                            }
                            const parts = [gl];
                            if (type === 'P') {
                                const wbs = account.wbs?.value || '';
                                return wbs ? parts.concat(wbs).join('_') : 'invalid';
                            } else if (type === 'K') {
                                const costCenter = account.costCenter?.value || '';
                                return costCenter ? parts.concat(costCenter).join('_') : 'invalid';
                            } else if (type === '2') {
                                const profitCenter = account.profitCenter?.value || '';
                                return profitCenter ? parts.concat(profitCenter).join('_') : 'invalid';
                            } else if (type === 'S') {
                                const wbs = account.wbs?.value || '';
                                const costCenter = account.costCenter?.value || '';
                                return (wbs && costCenter) ? parts.concat(wbs, costCenter).join('_') : 'invalid';
                            }
                            return 'invalid';
                        })
                        .join('|');
                } else {
                    dep = 'invalid';
                }
            } else {
                dep = accounts
                    .map((account, index) => {
                        const type = account.accountType?.value || '';
                        const gl = account.glNumber?.value || '';
                        if (!gl) {
                            return 'invalid';
                        }
                        const parts = [gl];
                        if (type === 'P') {
                            const wbs = account.wbs?.value || '';
                            return wbs ? parts.concat(wbs).join('_') : 'invalid';
                        } else if (type === 'K') {
                            const costCenter = account.costCenter?.value || '';
                            return costCenter ? parts.concat(costCenter).join('_') : 'invalid';
                        } else if (type === '2') {
                            const profitCenter = account.profitCenter?.value || '';
                            return profitCenter ? parts.concat(profitCenter).join('_') : 'invalid';
                        } else if (type === 'S') {
                            const wbs = account.wbs?.value || '';
                            const costCenter = account.costCenter?.value || '';
                            return (wbs && costCenter) ? parts.concat(wbs, costCenter).join('_') : 'invalid';
                        }
                        return 'invalid';
                    })
                    .join('|');
            }
            acc[material.id] = dep;
            return acc;
        }, {});
    }, [materials, areAccountSplitByMaterial, accountDetailsByMaterial, accounts]);


    const accountsDependency = useMemo(() => {
        if (areAccountSplitByMaterial) {
            return accountDetailsByMaterial
                .map((detail, detailIndex) => {
                    if (detail.accounts && detail.accounts.length > 0) {
                        return detail.accounts
                            .map((account, accIndex) => {
                                const type = account.accountType?.value || '';
                                const gl = account.glNumber?.value || '';
                                if (!gl) {
                                    return 'invalid';
                                }
                                const parts = [gl];
                                if (type === 'P') {
                                    const wbs = account.wbs?.value || '';
                                    return wbs ? parts.concat(wbs).join('_') : 'invalid';
                                } else if (type === 'K') {
                                    const costCenter = account.costCenter?.value || '';
                                    return costCenter ? parts.concat(costCenter).join('_') : 'invalid';
                                } else if (type === '2') {
                                    const profitCenter = account.profitCenter?.value || '';
                                    return profitCenter ? parts.concat(profitCenter).join('_') : 'invalid';
                                } else if (type === 'S') {
                                    const wbs = account.wbs?.value || '';
                                    const costCenter = account.costCenter?.value || '';
                                    return (wbs && costCenter) ? parts.concat(wbs, costCenter).join('_') : 'invalid';
                                }
                                return 'invalid';
                            })
                            .join('|');
                    }
                    return 'invalid';
                })
                .join('|');
        } else {
            return accounts
                .map((account, index) => {
                    const type = account.accountType?.value || '';
                    const gl = account.glNumber?.value || '';
                    if (!gl) {
                        return 'invalid';
                    }
                    const parts = [gl];
                    if (type === 'P') {
                        const wbs = account.wbs?.value || '';
                        return wbs ? parts.concat(wbs).join('_') : 'invalid';
                    } else if (type === 'K') {
                        const costCenter = account.costCenter?.value || '';
                        return costCenter ? parts.concat(costCenter).join('_') : 'invalid';
                    } else if (type === '2') {
                        const profitCenter = account.profitCenter?.value || '';
                        return profitCenter ? parts.concat(profitCenter).join('_') : 'invalid';
                    } else if (type === 'S') {
                        const wbs = account.wbs?.value || '';
                        const costCenter = account.costCenter?.value || '';
                        return (wbs && costCenter) ? parts.concat(wbs, costCenter).join('_') : 'invalid';
                    }
                    return 'invalid';
                })
                .join('|');
        }
    }, [accounts, areAccountSplitByMaterial, accountDetailsByMaterial]);

    const showTaxAmount =
        companyCode.value.substring(0, 2) === 'US' &&
        materials.some((m) => m.taxCode?.value === 'I1');

    const isTaxAmountDependencyMissing =
        !companyCode.value ||
        !purchaseOrganization.value ||
        !vendorCode ||
        !defaultPlant.value ||
        accountsDependency.includes('invalid');

    useEffect(() => {
        const fetchTaxAmount = async () => {
            setIsTaxAmountLoading(true);
            setMaterialsReady(false);
            try {
                const taxAmountResult = await getTaxAmountLambda(
                    props.context,
                    getTaxAmount,
                    JSON.stringify(currentPr),
                );
                const parsedResult = JSON.parse(taxAmountResult);
                const data = parsedResult.data;

                const matchedMaterials = materials.reduce((acc, material) => {
                    let materialDescription = material.name || '';
                    const dashIndex = materialDescription.indexOf('-');
                    if (dashIndex !== -1) {
                        materialDescription = materialDescription.substring(dashIndex + 1).trim();
                    }

                    const matQuantity = Number(material.quantity) || 0;
                    const matUnit = material.baseUnit || '';

                    const matchingLineItem = data.lineItems.find((lineItem) => {
                        const taxDescField = lineItem.flexibleFields.flexibleCodeFields.find(
                            (field) => field.fieldId === 1,
                        );
                        const taxDescription = taxDescField ? taxDescField.value : '';
                        const lineQuantity = Number(lineItem.quantity.value) || 0;
                        const lineUnit = lineItem.quantity.unitOfMeasure || '';

                        return (
                            taxDescription === materialDescription &&
                            lineQuantity === matQuantity &&
                            lineUnit === matUnit
                        );
                    });

                    if (matchingLineItem) {
                        acc.push({
                            material: material,
                            totalTax: matchingLineItem.totalTax,
                        });
                    }
                    return acc;
                }, []);

                updateAllMaterialsFunctional((prevMaterials) =>
                    produce(prevMaterials, (draft) => {
                        draft.forEach((material) => {
                            const match = matchedMaterials.find(
                                (item) => item.material.id === material.id,
                            );
                            if (match) {
                                material.taxAmount = match.totalTax;
                            }
                        });
                    }),
                );
            } catch (error) {
                console.error('Error fetching tax amount:', error);
            } finally {
                setMaterialsReady(true);
                setIsTaxAmountLoading(false);
            }
        };

        if (showTaxAmount && !isTaxAmountDependencyMissing) {
            fetchTaxAmount();
        }
    }, [companyCode.value, purchaseOrganization.value, vendorCode, defaultPlant.value, materialI1Signature, accountsDependency, materialTaxDependencies]);

    const listenItemsFromPunchout = () => {
        CloudObject.get(props.prTag).observe(props.context, PurchaseRequestDM.jsonProp, false)
            // Listen only the first two events
            // The first one is the pr without any change (as there is a value, it is automatically triggered), so it is skipped
            // The second one is the modified PR
            .pipe(skip(1), take(1)).subscribe((pr) => {
                const parsedPr = new PurchaseRequest(pr);
                syncCopiedItems();
                updateMaterials(parsedPr.materials);
                if (loadingMaterial) {
                    setLoadingMaterial(false);
                }
                if (storeLoadingMaterials) {
                    setStoreLoadingMaterials(false);
                }
            // No need to unsubscribe, as take(2) will automatically unsubscribe after the second event
            }).add(() => {
            // If we had copied items before opening the popup, we append them after the subscription end.
                appendCopiedItems();
            });
    };

    const handleSelectMaterial = (materialId) => {
        setSelectedMaterialIds((prevSelectedIds) => {
            if (prevSelectedIds.includes(materialId)) {
                return prevSelectedIds.filter((id) => id !== materialId);
            } else {
                return [...prevSelectedIds, materialId];
            }
        });
    };

    const updateSelectedMaterials = (updates) => {
        const newMaterials = produce(materials, (draft) => {
            draft.forEach((material) => {
                if (selectedMaterialIds.includes(material.id)) {
                    Object.entries(updates).forEach(([property, value]) => {
                        if (property === 'description') {
                            const editable = source === SOURCES.DYN || ['ZSIM', 'ZGNV', 'NLAG'].includes(material.type);
                            if (editable) {
                                material.description = value;
                            }
                        } else if (property === 'quantity') {
                            const parsedQuantity = parseFloat(value) || 0.0;
                            material.quantity = parsedQuantity;
                            if (material.freeItem) {
                                material.per = parsedQuantity * material.price;
                            }
                            if (!material.unit) {
                                material.unit = material.baseUnit;
                            }
                        } else if (property === 'currency') {
                            material.currency = value;
                            if (value !== 'USDN' && String(material.price).includes('.') && String(material.price).split('.')[1]?.length > 3) {
                                material.price = parseFloat(material.price).toFixed(3);
                            }
                        } else {
                            material[property] = value;
                        }
                    });
                }
            });
        });
        updateAllMaterials(newMaterials);
    };

    const handleTransportCostsChange = (checkedIndex) => {
        setResetSelection(false);
        setSelectedTransportCostMaterialId(null);
        switch (checkedIndex) {
        case TRANSPORT_OPTIONS_ENUM['Via Material line - Same supplier']:
            // transport cost with same supplier
            setLoadingTransportCost(true);
            addTransportCosts(() => setLoadingTransportCost(false), TRANSPORT_OPTIONS_ENUM['Via Material line - Same supplier']);
            break;
        case TRANSPORT_OPTIONS_ENUM['Via Material line - Supplier is different']:
            // transport cost with different supplier
            break;
        case TRANSPORT_OPTIONS_ENUM['Via Condition type']:
            // transport cost with condition Type
            //  addConditionTransportCost();
            setShowViaConditionPopup(true);
            break;
        }
    };

    const handlePageSelection = (newValue) => {
        setPage(newValue);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleTransportCostConditionConfirm = (selectedMaterialIds, materialId, conditions) => {
        addConditionTransportCost(()=>{
            setShowViaConditionPopup(false);
        }, selectedMaterialIds, materialId, conditions);
    };

    const handleOnClosePopup = ()=> {
        // check if we already have the transport set, if not reset the selection
        if ( !materials?.[0]?.transportCosts) {
            setResetSelection(true);
        }
        setShowViaConditionPopup(false);
    };

    const handleShowAddMaterialPopup = () => {
        if (source === SOURCES.CANDEX) {
            setLoadingMaterial(true);
            openCandexLambda(props.context, openCandex, props.prTag).then(() => {
                // We need to clear the event value, to avoid previous value if we open the popup several times
                props.context.remove('Close Candex popup');

                // Observe the PR, and the Close Candex popup
                const prObserver = CloudObject.get(props.prTag).observe(props.context, PurchaseRequestDM.jsonProp, false);
                const eventObserver = props.context.observe('Close Candex popup');
                merge(
                    prObserver.pipe(map((value) => ({source: 'prObserver', value}))),
                    eventObserver.pipe(map((value) => ({source: 'eventObserver', value}))),
                )
                    // Listen only the first two events
                    // The first one is the pr without any change (as there is a value, it is automatically triggered)
                    // The second one is either the modified PR, with the new materials, or the close popup event
                    .pipe(take(2)).subscribe((event) => {
                        if (event.source === 'prObserver') { // If it is the modified PR, we update the line items list
                            const parsedPr = new PurchaseRequest(event.value);
                            if (parsedPr.materials.length !== materials.length) {
                                syncCopiedItems();
                                updateMaterials(parsedPr.materials);
                                setLoadingMaterial(false);
                            }
                        } else { // If it is the close popup event, we simply stop the loading state
                            setLoadingMaterial(false);
                        }
                        // No need to unsubscribe, as take(2) will automatically unsubscribe after the second event
                    }).add(() => {
                        // If we had copied items before opening the popup, we append them after the subscription end.
                        appendCopiedItems();
                    });
            });
        } else if (source === SOURCES.NESTBUY) {
            setLoadingMaterial(true);
            setAddMaterialLoading(true);

            const initialMaterialsCount = materials.length;

            /**
             * Stops polling for materials added from the NestBuy catalog.
             */
            const stopNestBuyMaterialPolling = () => {
                isNestBuyMaterialPollingRef.current = false;
                if (nestBuyMaterialPollTimeoutRef.current) {
                    clearTimeout(nestBuyMaterialPollTimeoutRef.current);
                    nestBuyMaterialPollTimeoutRef.current = null;
                }
                if (nestBuyMaterialPollMaxTimeoutRef.current) {
                    clearTimeout(nestBuyMaterialPollMaxTimeoutRef.current);
                    nestBuyMaterialPollMaxTimeoutRef.current = null;
                }
            };

            /**
             * Stops polling after the maximum duration and resets the loading state.
             */
            const stopNestBuyMaterialPollingDueToTimeout = () => {
                stopNestBuyMaterialPolling();
                setLoadingMaterial(false);
                setAddMaterialLoading(false);
                appendCopiedItems();
            };

            /**
             * Polls the PR until new materials are detected, then updates the line items list.
             * @return {Promise<void>}
             */
            const pollForNewMaterials = async () => {
                if (!isNestBuyMaterialPollingRef.current) {
                    return;
                }

                try {
                    const co = (await Query.fromTag(props.prTag, PurchaseRequestDM).execute()).getFirst();
                    const parsedPr = new PurchaseRequest(co.getJson());

                    if (parsedPr.materials.length !== initialMaterialsCount) {
                        stopNestBuyMaterialPolling();
                        syncCopiedItems();
                        updateMaterials(parsedPr.materials);
                        setLoadingMaterial(false);
                        setAddMaterialLoading(false);
                        appendCopiedItems();
                    } else {
                        nestBuyMaterialPollTimeoutRef.current = setTimeout(pollForNewMaterials, NESTBUY_MATERIAL_POLL_INTERVAL_MS);
                    }
                } catch (error) {
                    console.error('Error polling for new materials:', error);
                    if (isNestBuyMaterialPollingRef.current) {
                        nestBuyMaterialPollTimeoutRef.current = setTimeout(pollForNewMaterials, NESTBUY_MATERIAL_POLL_INTERVAL_MS);
                    }
                }
            };

            openNestBuyCatalogLambda(props.context, openNestBuyCatalog, props.prTag)
                .then(() => {
                    stopNestBuyMaterialPolling();
                    isNestBuyMaterialPollingRef.current = true;
                    nestBuyMaterialPollTimeoutRef.current = setTimeout(pollForNewMaterials, NESTBUY_MATERIAL_POLL_INTERVAL_MS);
                    nestBuyMaterialPollMaxTimeoutRef.current = setTimeout(
                        stopNestBuyMaterialPollingDueToTimeout,
                        NESTBUY_MATERIAL_POLL_MAX_DURATION_MS,
                    );
                });
        } else if (source === SOURCES.PUNCHOUT || sourceToDisplay === SOURCES_TO_DISPLAY.PUNCHOUT) {
            setLoadingMaterial(true);
            setAddMaterialLoading(true);

            getPunchoutCatalogsInfoLambda(props.context, getPunchOutCatalogs)
                .then((r) => {
                    const results = JSON.parse(r[0]);
                    if (results.some((c) => (c['vendor_code'] === vendorCode) && (c['company_code'] === companyCode.value))) {
                        handleAccessCatalog();
                    } else {
                        setErrorMsg(`The PunchOut catalog for supplier ${vendorCode} - ${vendorName} is no longer available.`);
                        setErrorPopupOpen(true);
                        setLoadingMaterial(false);
                    }
                })
                .finally(() => setAddMaterialLoading(false));
        } else {
            setShowAddMaterialPopup(true);
        }
    };

    const handleAccessCatalog = () => {
        getPunchoutCatalogURLLambda(
            props.context,
            getPunchOutCatalogURL,
            vendorCode,
            vendorName,
            companyCode.value,
            companyCode,
            purchaseOrganization,
            defaultPlant,
            defaultDeliveryAddress,
            materials[0].materialCode,
            materials[0].l3Category,
            EPRId,
            'punchout_add_material')
            .then((r) => {
                const url = r[0];
                window.open(url, '_blank').focus();

                listenItemsFromPunchout();
            })
            .catch((err) => {
                console.error(err);
                setLoadingMaterial(false);
            });
    };

    const handleOnCloseAddMaterialPopup = () => {
        setShowAddMaterialPopup(false);
    };

    const handleCloseErrorPopup = () => {
        setErrorPopupOpen(false);
    };

    const filteredMaterials = rowsPerPage === -1 ? materialsToShow : materialsToShow.slice(rowsPerPage * page, rowsPerPage * (page + 1));

    return (
        <div>
            <div className='pr-editor-item-details-header'>
                <p className={'pr-editor-section-title'}>Item details</p>
                <div className={'pr-editor-item-details-checkbox'}>
                    {loadingTransportCost && <CircularProgress size={20}/>}
                    <AddTransportCostComponent
                        onChange={handleTransportCostsChange}
                        disabled={isDisabled('lineItems.addTransportCost')}
                        checked={materials?.[materials.length - 1]?.transportCostType}
                        userHasAccessToProperties={userHasAccessToProperties}
                        resetSelection={resetSelection}
                        selectedMaterialIds={selectedMaterialIds}
                        prTag={props.prTag}
                        materials={materials}
                    />
                </div>
            </div>
            <div style={{display: 'flex'}}>
                <div className={'pr-editor-item-details-columnTitles'}>
                    {materials.length > 1 && (
                        <div className={'pr-editor-item-details-element'} style={{flexGrow: 1, marginLeft: '-0.5rem'}}>
                            <CheckboxWithOptionsLineItem
                                setSelectedMaterialIds={setSelectedMaterialIds}
                                selectedMaterialIds={selectedMaterialIds}
                                materials={materials}
                                filteredMaterials={filteredMaterials}
                                disabled={isDisabled('lineItems.bulkSelect')}
                            />
                        </div>
                    )}
                    <div className={'pr-editor-item-details-element'}
                        style={{flex: '0 0 20px', textAlign: 'center'}}>#
                    </div>
                    <div className={'pr-editor-item-details-element'} style={{flex: '4 1 19%'}}>Material *</div>
                    <div className={'pr-editor-item-details-element'} style={{flex: '4 1 17%'}}>Description *</div>
                    <div className={'pr-editor-item-details-element'} style={{flex: '1 4 6%'}}>Quantity *</div>
                    <div className={'pr-editor-item-details-element'} style={{flex: '2 2 8%'}}>Unit *</div>
                    <div className={'pr-editor-item-details-element'} style={{flex: '1 4 6%'}}>Price *</div>
                    <div className={'pr-editor-item-details-element'} style={{flex: '2 2 8%'}}>Currency *</div>
                    <div className={'pr-editor-item-details-element'} style={{flex: '1 4 6%'}}>Per *</div>
                    <div className={'pr-editor-item-details-element'}
                        style={{display: 'flex', gap: '5px', flex: '0 0 140px'}}>
                        <div>{`Tax code${Config.getParameter('ENV') !== 'codev' ? ' *' : ''}`}</div>
                    </div>
                    <div className={'pr-editor-item-details-element'} style={{flex: '0 0 110px'}}>Subtotal</div>
                    <div className={'pr-editor-item-details-element'} style={{flex: '0 0 25px'}}/>
                </div>
                {!isDisabled('lineItem.rowActions') && (
                    <div style={{width: '40px'}}/>
                )}
            </div>
            <div style={{position: 'relative', height: '100%', width: '100%'}}>
                {(loadingMaterial || storeLoadingMaterials) &&
                    <div className={'pr-editor-item-details-loading'}>
                        <CircularProgress size={50}/>
                        <div>Getting new line items...</div>
                    </div>}
                {filteredMaterials.map((material, idx) => (
                    <LineItem
                        key={`materialInfo-${material.id}-${idx}`}
                        materialId={material.id}
                        displayedIndex={source === SOURCES.CANDEX ? idx : undefined}
                        context={props.context}
                        prTag={props.prTag}
                        selectedMaterialIds={selectedMaterialIds}
                        onSelectMaterial={handleSelectMaterial}
                        setSelectedMaterialIds={setSelectedMaterialIds}
                        updateSelectedMaterials={updateSelectedMaterials}
                        setShowViaConditionPopup={()=>{
                            setShowViaConditionPopup(true);
                        }}
                        setSelectedTransportCostMaterialId={setSelectedTransportCostMaterialId}
                        setIsItemUngrouped={props.setIsItemUngrouped}
                        loadingCheckDependency={loadingCheckDependency}
                        isTaxAmountLoading={isTaxAmountLoading}
                        isTaxAmountDependencyMissing={isTaxAmountDependencyMissing}
                        materialTaxDependencies={materialTaxDependencies}
                    />
                ))}
            </div>
            <div className={'pr-editor-item-details--action-container'}>
                {showAddMaterial && (
                    <LoadingButton
                        className="pr-editor-item-details-add-btn"
                        startIcon={<AddIcon />}
                        sx={{
                            color: ezprOrange,
                            borderColor: ezprOrange,
                            border: '2px solid',
                            borderRadius: '20px',
                            marginTop: '0.5rem',
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                        }}
                        onClick={handleShowAddMaterialPopup}
                        disabled={isDisabled('lineItems.addMaterial')}
                        loading={addMaterialLoading}
                        loadingPosition={'start'}
                    >
                        Add material
                    </LoadingButton>
                )}
                <TablePaginationWithPageSelector
                    page={page}
                    pageNum={pageNum}
                    count={materials.length}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[2, 5, 10, 20, 50, 100, {label: 'All', value: -1}]}
                    handlePageSelection={handlePageSelection}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </div>
            {showViaConditionPopup && (
                <TransportCostsConditionTypePopup
                    prTag = {props.prTag}
                    open={showViaConditionPopup}
                    onClose={handleOnClosePopup}
                    onConfirm={handleTransportCostConditionConfirm}
                    selectedMaterialIds={selectedMaterialIds}
                    context = {props.context}
                    materialId={selectedTransportCostMaterialId}
                />
            )}
            {showAddMaterialPopup && (
                <AddMaterialPopup
                    prTag = {props.prTag}
                    context = {props.context}
                    open={showAddMaterialPopup}
                    onClose={handleOnCloseAddMaterialPopup}
                />
            )}
            <ErrorPopup
                onClose={handleCloseErrorPopup}
                open={errorPopupOpen}
                errorMessages={[errorMsg]}
                title={`Impossible to open the supplier's catalog`}
            />
        </div>
    );
}

LineItems.propTypes = {
    prTag: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    setIsItemUngrouped: PropTypes.func,
};

LineItems.taxCodeInfo = 'If you do not have this information, this field can remain empty to be completed by the Requester later on.';
